import { Controller } from '@hotwired/stimulus'
import { easepick, AmpPlugin, LockPlugin } from '@easepick/bundle'
import Imask from 'imask'
import { stimulus } from '~/init'
import easepickCss from '@easepick/bundle/dist/index.css?inline'
import brandCss from '~/stylesheets/vendors/easepick.css?inline'

const DEFAULT_MIN_YEAR = 1900
const MAX_YEAR = new Date().getFullYear()
const VIEWPORT_MARGIN = 8
const DISPLAY_FORMAT = 'MM/DD/YYYY'

// The picker renders inside a shadow root, so page-level stylesheets never
// reach it — the CSS has to be passed in as text and injected there.
export default class EasepickController extends Controller {
  initialize () {
    this.binds = { syncFromElement: this.syncFromElement.bind(this) }
  }

  connect () {
    this.#init()
  }

  disconnect () {
    this.picker?.destroy()
    this.#destroyMask()
  }

  show () {
    this.picker?.show()
  }

  // Re-creates the picker, picking up an updated data-easepick-min-date
  // attribute (used to lock the "to" field once a "from" date is chosen).
  reload () {
    this.picker?.destroy()
    this.#destroyMask()
    this.#init()
  }

  onSelect (e) {
    this.syncFromElement()
    this.dispatch('onSelect', { detail: e.detail })
  }

  // Mirrors the visible MM/DD/YYYY text into the hidden field as the ISO date
  // the server expects. A half-typed date has no ISO form, so it submits blank
  // rather than something the server would have to guess at.
  // The picker writes the field directly, and period_controller snaps the end
  // date forward the same way; neither fires an input event, so the mask has to
  // be told to re-read the element before the hidden value is derived from it.
  syncFromElement () {
    this.mask?.updateValue()
    this.syncValue()
  }

  syncValue () {
    if (!this.valueField) return

    const [ month, day, year ] = this.element.value.split('/')
    this.valueField.value = year?.length === 4 ? `${year}-${month}-${day}` : ''
  }

  #init () {
    const minDate = this.element.dataset.easepickMinDate
    const minYear = Number(this.element.dataset.easepickMinYear) || DEFAULT_MIN_YEAR
    this.minYear = minYear
    const lock = { maxDate: new Date() }
    if (minDate) lock.minDate = minDate

    this.picker = new easepick.Core({
      element: this.element,
      css: easepickCss + brandCss,
      format: DISPLAY_FORMAT,
      autoApply: true,
      grid: 1,
      calendars: 1,
      readonly: false,
      zIndex: 50,
      plugins: [ AmpPlugin, LockPlugin ],
      AmpPlugin: {
        dropdown: { minYear, maxYear: MAX_YEAR, months: true, years: true },
        darkMode: false
      },
      LockPlugin: lock,
      setup: (picker) => {
        picker.on('select', this.onSelect.bind(this))
        picker.on('show', () => this.#keepWithinViewport(picker))
      }
    })

    this.#initializeMask()

    this.element.dispatchEvent(new CustomEvent('easepick:initialized', {
      bubbles: true,
      detail: { picker: this.picker }
    }))
  }

  get valueField () {
    return document.getElementById(this.element.dataset.easepickValueId)
  }

  #initializeMask () {
    this.mask = Imask(this.element, {
      mask: DISPLAY_FORMAT,
      blocks: {
        DD: { mask: Imask.MaskedRange, from: 1, to: 31, maxLength: 2 },
        MM: { mask: Imask.MaskedRange, from: 1, to: 12, maxLength: 2 },
        YYYY: { mask: Imask.MaskedRange, from: this.minYear, to: MAX_YEAR }
      },
      autofix: true,
      lazy: true,
      overwrite: true
    })

    this.mask.on('accept', () => this.syncValue())

    // Once a whole date is typed, move the calendar onto it so opening the
    // picker does not land the user back on today.
    this.mask.on('complete', () => {
      this.picker.gotoDate(this.mask.value)
      this.picker.setDate(this.mask.value)
    })

    this.element.addEventListener('change', this.binds.syncFromElement)
  }

  #destroyMask () {
    this.element.removeEventListener('change', this.binds.syncFromElement)
    this.mask?.destroy()
    this.mask = null
  }

  #keepWithinViewport (picker) {
    const container = picker.ui?.container
    if (!container) return

    const { right } = container.getBoundingClientRect()
    const overflow = right - (window.innerWidth - VIEWPORT_MARGIN)
    if (overflow <= 0) return

    const left = parseFloat(container.style.left) || 0
    container.style.left = `${left - overflow}px`
  }
}

stimulus.register('easepick', EasepickController)
